import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { History } from "lucide-react";
import { getSubAccountActivityLog } from "../../services/subAccountService";
import Card from "../common/Card";
import Loader from "../common/Loader";
import Button from "../common/Button";
import EmptyState from "../common/EmptyState";
import { color } from "../../styles/theme";

const PAGE_SIZE = 20;

const formatDateTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

/**
 * Owner-facing view of everything every Sub-Account has done — reads the
 * existing ActionLog table (already written by every write action via
 * ISafeLogger) through the new GET SubAccount/ActivityLog endpoint. Nothing
 * for a Sub-Account to see here — owner-only, per spec ch.11.2/11.4/11.12
 * ("all relevant actions are assigned in the logs to the Sub-Account and the
 * Business Account").
 */
const SubAccountActivityLog = () => {
  const { t } = useTranslation(["subAccounts", "common"]);
  const [entries, setEntries] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = (pageNumber = 1) => {
    (pageNumber === 1 ? setLoading : setLoadingMore)(true);
    getSubAccountActivityLog(pageNumber, PAGE_SIZE)
      .then((res) => {
        const list = res.data?.Result || [];
        setEntries((prev) => (pageNumber === 1 ? list : [...prev, ...list]));
        setMeta(res.data?.Metadata || null);
        setPage(pageNumber);
      })
      .catch(() => toast.error(t("activityLog.loadError")))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasMore = meta && entries.length < (meta.TotalCount ?? 0);

  if (loading) return <Loader />;

  return (
    <div>
      {entries.length === 0 ? (
        <EmptyState icon={History} text={t("activityLog.empty")} />
      ) : (
        <div style={s.list}>
          {entries.map((entry) => (
            <Card key={entry.Id} style={s.row}>
              <div style={s.info}>
                <div style={s.topLine}>
                  <span style={s.actor}>{entry.SubAccountName}</span>
                  <span style={s.actionType}>{entry.ActionType}</span>
                </div>
                {entry.Description && <p style={s.description}>{entry.Description}</p>}
                <span style={s.timestamp}>{formatDateTime(entry.CreatedAt)}</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {hasMore && (
        <div style={s.loadMoreRow}>
          <Button variant="secondary" onClick={() => load(page + 1)} disabled={loadingMore}>
            {loadingMore ? t("common:status.loading") : t("activityLog.loadMore")}
          </Button>
        </div>
      )}
    </div>
  );
};

const s = {
  list: { display: "flex", flexDirection: "column", gap: 8, maxWidth: 680 },
  row: { padding: "12px 16px" },
  info: { display: "flex", flexDirection: "column", gap: 3 },
  topLine: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  actor: { fontSize: 13.5, fontWeight: 700, color: color.textPrimary },
  actionType: {
    fontSize: 10.5, fontWeight: 700, color: color.primary600, background: color.primary50,
    padding: "2px 8px", borderRadius: 999, letterSpacing: 0.2,
  },
  description: { margin: "2px 0 0", fontSize: 12.5, color: color.textSecondary, lineHeight: 1.5 },
  timestamp: { fontSize: 11, color: color.textMuted },
  loadMoreRow: { display: "flex", justifyContent: "center", marginTop: 16 },
};

export default SubAccountActivityLog;
